import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Dialog, DialogTitle, DialogContent, IconButton, Alert, Grid } from '@mui/material';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import CloseIcon from '@mui/icons-material/Close';
import LoadingState from './loadingstate';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ClimateCorrelationAnalysis = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(5, 'years').startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().startOf('month'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  
  const minDate = dayjs('2015-01-01');
  const maxDate = dayjs();
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const start_date = startDate.format('YYYY-MM-DD');
        const end_date = endDate.format('YYYY-MM-DD');
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/analysis/climate-fire-correlation?start_date=${start_date}&end_date=${end_date}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch climate-fire correlation data');
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (startDate && endDate && startDate.isSameOrBefore(endDate)) {
      fetchData();
    } else if (startDate && endDate) {
      setError("Start date must be on or before end date.");
    }
  }, [startDate, endDate]);

  const handleExpandChart = (chartType) => {
    setExpandedChart(chartType);
  };
  
  const handleCloseDialog = () => {
    setExpandedChart(null);
  };

  const CorrelationExplanation = ({ value }) => (
    <Alert 
      severity="info" 
      sx={{ 
        mt: 2,
        '& .MuiAlert-message': {
          width: '100%',
          textAlign: 'center'
        }
      }}
    >
      <Typography variant="caption">
        <strong>Correlation: {value ? value.toFixed(3) : 'N/A'}</strong>
        <br />A value near +1.0 means more fires tend to happen with higher values (e.g., higher temp).
        <br />A value near -1.0 means more fires tend to happen with lower values (e.g., lower rain).
        <br />A value near 0.0 means there is no clear connection.
      </Typography>
    </Alert>
  );

  return (
    <Box>
      <Grid container spacing={3} alignItems="center" mb={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6">Climate-Fire Correlation Analysis</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Month"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue.startOf('month'))}
              views={['year', 'month']}
              format="MMMM YYYY"
              sx={{ width: '100%' }}
              minDate={minDate}
              maxDate={maxDate}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Month"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue.startOf('month'))}
              views={['year', 'month']}
              format="MMMM YYYY"
              sx={{ width: '100%' }}
              minDate={minDate}
              maxDate={maxDate}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%',
            textAlign: 'center'
          }
        }}
      >
        <Typography variant="caption">
          This data is based on monthly averages for Sarawak and is automatically updated on the 1st of every month.
        </Typography>
      </Alert>
      
      <LoadingState loading={loading} error={error}>
        <Box>
          {/* Row 1: Temperature and Precipitation - side by side */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              mb: 3,
              justifyContent: 'space-between',
              alignItems: 'stretch'
            }}
          >
            {/* Temperature Chart */}
            <Paper 
              variant='outlined'
              sx={{ 
                p: { xs: 2, md: 3 }, 
                cursor: 'pointer', 
                flex: { xs: 'none', md: '1 1 48%' },
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => handleExpandChart('temp')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">Temperature vs. Fire Incidents</Typography>
                <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
              </Box>
              <Box sx={{ height: { xs: 300, md: 350 }, flex: 1 }}>
                <Scatter
                  data={data?.temperatureVsFires}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Average Temperature (°C)'
                        }
                      },
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Fire Alerts'
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `Temp: ${context.parsed.x.toFixed(1)}°C, Fires: ${context.parsed.y}`;
                          }
                        }
                      }
                    }
                  }}
                />
              </Box>
              <CorrelationExplanation value={data?.temperatureCorrelation} />
            </Paper>

            {/* Precipitation Chart */}
            <Paper 
              variant='outlined'
              sx={{ 
                p: { xs: 2, md: 3 }, 
                cursor: 'pointer', 
                flex: { xs: 'none', md: '1 1 48%' },
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => handleExpandChart('precip')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">Precipitation Impact</Typography>
                <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
              </Box>
              <Box sx={{ height: { xs: 300, md: 350 }, flex: 1 }}>
                <Line 
                  data={data?.precipitationTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Incidents'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Precipitation (mm)'
                        },
                        beginAtZero: true,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              </Box>
              <CorrelationExplanation value={data?.precipitationCorrelation} />
            </Paper>
          </Box>

          {/* Row 2: Humidity and Wind - side by side */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              mb: 3,
              justifyContent: 'space-between',
              alignItems: 'stretch'
            }}
          >
            {/* Humidity Chart */}
            <Paper 
              variant='outlined'
              sx={{ 
                p: { xs: 2, md: 3 }, 
                cursor: 'pointer', 
                flex: { xs: 'none', md: '1 1 48%' },
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => handleExpandChart('humidity')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">Humidity Impact</Typography>
                <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
              </Box>
              <Box sx={{ height: { xs: 300, md: 350 }, flex: 1 }}>
                <Line 
                  data={data?.humidityTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Incidents'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Relative Humidity (%)'
                        },
                        min: 80,
                        max: 100,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              </Box>
              <CorrelationExplanation value={data?.humidityCorrelation} />
            </Paper>

            {/* Wind Chart */}
            <Paper 
              variant='outlined'
              sx={{ 
                p: { xs: 2, md: 3 }, 
                cursor: 'pointer', 
                flex: { xs: 'none', md: '1 1 48%' },
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => handleExpandChart('wind')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">Wind Speed Impact</Typography>
                <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
              </Box>
              <Box sx={{ height: { xs: 300, md: 350 }, flex: 1 }}>
                <Line 
                  data={data?.windTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Incidents'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Wind Speed (m/s)'
                        },
                        beginAtZero: true,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              </Box>
              <CorrelationExplanation value={data?.windCorrelation} />
            </Paper>
          </Box>

          {/* Row 3: Monthly Combined Chart - full width */}
          <Paper 
            sx={{ p: { xs: 2, md: 3 }, cursor: 'pointer' }}
            onClick={() => handleExpandChart('monthly')}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">Monthly Climate vs Fire</Typography>
              <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
            </Box>
            <Box height={{ xs: 300, md: 400 }}>
              <Bar
                data={data?.monthlyComparison}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'index',
                    intersect: false,
                  },
                  scales: {
                    y: {
                      type: 'linear',
                      display: true,
                      position: 'left',
                      title: {
                        display: true,
                        text: 'Fire Alerts'
                      },
                      beginAtZero: true,
                    },
                    y1: {
                      type: 'linear',
                      display: true,
                      position: 'right',
                      title: {
                        display: true,
                        text: 'Climate Variables'
                      },
                      beginAtZero: true,
                      grid: {
                        drawOnChartArea: false,
                      },
                    },
                  }
                }}
              />
            </Box>
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                  textAlign: 'center'
                }
              }}
            >
              <Typography variant="caption">
                This chart shows monthly fire alerts (line) against temperature (blue), precipitation (green, divided by 10), and wind speed (purple) in m/s.
              </Typography>
            </Alert>
          </Paper>
        </Box>
        
        {/* Dialog for expanded charts */}
        <Dialog
          open={!!expandedChart}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {expandedChart === 'temp' && 'Temperature vs. Fire Incidents'}
            {expandedChart === 'precip' && 'Precipitation Impact on Fire Incidents'}
            {expandedChart === 'humidity' && 'Humidity Impact on Fire Incidents'}
            {expandedChart === 'wind' && 'Wind Speed Impact on Fire Incidents'}
            {expandedChart === 'monthly' && 'Monthly Climate Variables vs Fire Alerts'}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box height={500}>
              {expandedChart === 'temp' && (
                <Scatter
                  data={data?.temperatureVsFires}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Average Temperature (°C)'
                        }
                      },
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Fire Alerts'
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `Temp: ${context.parsed.x.toFixed(1)}°C, Fires: ${context.parsed.y}`;
                          }
                        }
                      }
                    }
                  }}
                />
              )}
              {expandedChart === 'precip' && (
                <Line 
                  data={data?.precipitationTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Incidents'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Precipitation (mm)'
                        },
                        beginAtZero: true,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              )}
              {expandedChart === 'humidity' && (
                <Line 
                  data={data?.humidityTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Incidents'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Relative Humidity (%)'
                        },
                        min: 80,
                        max: 100,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              )}
              {expandedChart === 'wind' && (
                <Line 
                  data={data?.windTrend}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Incidents'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Wind Speed (m/s)'
                        },
                        beginAtZero: true,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              )}
              {expandedChart === 'monthly' && (
                <Bar
                  data={data?.monthlyComparison}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Fire Alerts'
                        },
                        beginAtZero: true,
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Climate Variables'
                        },
                        beginAtZero: true,
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    }
                  }}
                />
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </LoadingState>
    </Box>
  );
};

export default ClimateCorrelationAnalysis;